// Un lanzamiento convertido en un árbol de carpetas — ADR-2026-09-14-lanzamiento-a-carpetas.
//
// Es la salida del destino "Otra plataforma": no hay tienda a la que publicar, así
// que lo que se entrega es un ZIP con las fotos ordenadas como las pondría una
// persona a mano, para subirlas a donde sea.
//
//   Remeras/
//     Oversize/
//       Blusa Magic/
//         blusa_magic_azul_1.jpg
//         blusa_magic_azul_2.jpg
//         blusa_magic_roja_1.jpg
//
// Todo lo de acá es puro: arma la LISTA de rutas. Bajar los archivos y comprimir
// es de `descargarComoZip`, que ya existía.
//
// Dos vocabularios distintos a propósito:
//
//   · Las CARPETAS llevan el nombre como lo escribió el usuario — "Remeras Niño",
//     con su tilde y su mayúscula. Es lo que va a leer navegando el ZIP, y
//     "Remeras Nino" se lee peor. Solo se les saca lo que un sistema de archivos
//     no admite.
//   · Los ARCHIVOS van en minúscula, sin acentos y con guiones bajos
//     (`blusa_magic_azul_1`). Un nombre de archivo sobrevive a que lo arrastren
//     fuera de su carpeta, lo suban a un importador o lo manden por mail, y ahí
//     los acentos y los espacios son un problema real.
import type { CategoriaDeTienda, ProductoDeLanzamiento } from "./lanzamientosApi";
import { partesDelCamino } from "./arbolDeCategorias";
import { etiquetaDeVariante } from "./variantes";

/** Dónde caen los productos que no tienen ninguna categoría tildada. */
export const SIN_CATEGORIA = "Sin categoría";

/** Cuando no se le puede sacar ni una letra al nombre. */
const SIN_NOMBRE = "producto";

/** Un nombre largo rompe la ruta en Windows (tope de 260 caracteres). */
const MAX_LARGO = 60;

/**
 * Texto listo para ser nombre de archivo: minúsculas, sin acentos y con guiones
 * bajos. "Blusa Magic" → "blusa_magic".
 */
export function aSlug(texto: string | null | undefined): string {
  const limpio = (texto ?? "")
    .normalize("NFD")
    // El rango de las marcas de acento combinantes, por CÓDIGO y no por carácter:
    // escritos literales son invisibles en el editor y ya se corrompieron una vez
    // al copiarse de un lado a otro.
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return limpio.slice(0, MAX_LARGO) || SIN_NOMBRE;
}

/**
 * Texto listo para ser nombre de CARPETA: se respeta como lo escribió el usuario
 * y solo se le saca lo que rompe una ruta.
 *
 * Los caracteres prohibidos se reemplazan por un espacio en vez de borrarse:
 * "Azul/M" tiene que quedar "Azul M" y no "AzulM". El punto al final lo saca
 * Windows solo, así que se saca acá para que el nombre sea el mismo en los dos.
 */
export function aCarpeta(texto: string | null | undefined): string {
  const limpio = (texto ?? "")
    // Los de control y los que Windows prohíbe en un nombre. Por CÓDIGO: escrito
    // como rango de caracteres, `[ -<...]` iba de 0x20 a 0x3C y se comía los
    // dígitos y media puntuación.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f<>:"/\\|?*]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\.+$/, "")
    .trim();
  return limpio.slice(0, MAX_LARGO) || SIN_NOMBRE;
}

/** Un archivo del ZIP: de dónde sale y dónde va. */
export interface ArchivoDelZip {
  /** La ruta completa adentro del ZIP, con sus carpetas. */
  ruta: string;
  /** El archivo en Storage, que es lo que hay que bajar. */
  fileName: string;
  /** La creación detrás de la foto, para marcarla descargada; `null` si la subió a mano. */
  imagenGeneradaId: string | null;
}

/** La extensión del archivo original. La real la decide después el contenido. */
function extensionDe(fileName: string): string {
  const punto = fileName.lastIndexOf(".");
  if (punto <= 0 || punto === fileName.length - 1) return ".jpg";
  const ext = fileName.slice(punto + 1).toLowerCase();
  return /^[a-z0-9]{2,5}$/.test(ext) ? `.${ext}` : ".jpg";
}

/**
 * Las carpetas de un producto, de la raíz a la suya: categoría, subcategorías y
 * el producto.
 *
 * Va la PRIMERA categoría y no todas: este destino admite una sola por producto
 * (`unaSolaCategoria`), justamente para que el árbol de carpetas sea el árbol de
 * categorías y cada foto exista una sola vez en el ZIP.
 */
export function carpetasDeProducto(
  p: Pick<ProductoDeLanzamiento, "nombre" | "categorias">,
  arbol: readonly CategoriaDeTienda[],
): string[] {
  const primera = p.categorias[0];
  const camino = primera ? partesDelCamino(arbol, primera).map(aCarpeta) : [];
  // Una categoría que ya no existe en el árbol deja el camino vacío: es el mismo
  // caso que no tener ninguna.
  const base = camino.length > 0 ? camino : [SIN_CATEGORIA];
  return [...base, aCarpeta(p.nombre)];
}

/**
 * Qué variante muestra esta foto, o `null`.
 *
 * NO mira `imagenesPorVariante`: en este destino las carpetas SON la separación
 * por variante y no hay interruptor que apagar
 * (ADR-2026-09-14-en-el-zip-las-fotos-van-siempre-por-variante). Antes se
 * consultaba, y un producto con el flag apagado —de otro canal, o de antes de
 * que el interruptor desapareciera— amontonaba los tres colores en una carpeta
 * sola aunque cada foto supiera de qué variante era.
 *
 * La foto que no está asignada a ninguna variante sigue cayendo en la carpeta
 * del producto: no es de ninguna, y no hay dónde más ponerla.
 */
function varianteDeLaFoto(
  p: ProductoDeLanzamiento,
  foto: ProductoDeLanzamiento["imagenes"][number],
): { etiqueta: string } | null {
  if (!foto.lanzamientoVarianteId) return null;
  const orden = [...p.variantes].sort((a, b) => a.orden - b.orden);
  const i = orden.findIndex((v) => v.id === foto.lanzamientoVarianteId);
  if (i < 0) return null;
  return { etiqueta: etiquetaDeVariante(orden[i]!, i) };
}

/**
 * El ZIP entero, como lista de archivos.
 *
 * El número de cada foto (`_1`, `_2`) cuenta DENTRO de su variante y en el orden
 * en que están las fotos: es lo que hace que la primera de cada color sea la
 * `_1`, que es la que un importador suele tomar de portada.
 */
export function planDeExport(
  productos: readonly ProductoDeLanzamiento[],
  arbol: readonly CategoriaDeTienda[],
): ArchivoDelZip[] {
  const archivos: ArchivoDelZip[] = [];
  // Dos productos que se llaman igual en la misma categoría tendrían la misma
  // carpeta, y sus fotos se mezclarían sin que nadie se entere. El segundo va a
  // "Blusa Magic (2)".
  const carpetasTomadas = new Map<string, string>();
  const rutasTomadas = new Set<string>();

  for (const p of productos) {
    if (p.imagenes.length === 0) continue;

    const partes = carpetasDeProducto(p, arbol);
    let carpeta = partes.join("/");
    const deQuienEs = carpetasTomadas.get(carpeta.toLowerCase());
    if (deQuienEs !== undefined && deQuienEs !== p.id) {
      let n = 2;
      while (carpetasTomadas.has(`${carpeta} (${n})`.toLowerCase())) n += 1;
      carpeta = `${carpeta} (${n})`;
    }
    carpetasTomadas.set(carpeta.toLowerCase(), p.id);

    const slugProducto = aSlug(p.nombre);
    const contadores = new Map<string, number>();

    for (const foto of p.imagenes) {
      if (!foto.fileName) continue;
      const variante = varianteDeLaFoto(p, foto);
      const slugVariante = variante ? aSlug(variante.etiqueta) : "";
      const base = slugVariante ? `${slugProducto}_${slugVariante}` : slugProducto;
      const n = (contadores.get(base) ?? 0) + 1;
      contadores.set(base, n);

      const subcarpeta = variante ? `/${aCarpeta(variante.etiqueta)}` : "";
      const ext = extensionDe(foto.fileName);
      let ruta = `${carpeta}${subcarpeta}/${base}_${n}${ext}`;
      // Red de seguridad: dos rutas iguales se pisan en silencio adentro del ZIP.
      if (rutasTomadas.has(ruta.toLowerCase())) {
        let extra = 2;
        while (rutasTomadas.has(`${carpeta}${subcarpeta}/${base}_${n}_${extra}${ext}`.toLowerCase())) {
          extra += 1;
        }
        ruta = `${carpeta}${subcarpeta}/${base}_${n}_${extra}${ext}`;
      }
      rutasTomadas.add(ruta.toLowerCase());
      archivos.push({ ruta, fileName: foto.fileName, imagenGeneradaId: foto.imagenGeneradaId });
    }
  }

  return archivos;
}

/** El nombre del ZIP que se baja. */
export function nombreDelZip(nombreDelLanzamiento: string): string {
  return `${aSlug(nombreDelLanzamiento)}.zip`;
}
